import LazySection from "@/components/ui/LazySection";
import Layout from "@/components/layout/Layout";
import { Helmet } from "react-helmet-async";
import HeroSlider, { HeroSlide } from "@/components/ui/HeroSlider";
import { Button } from "@/components/ui/button";
import { Link } from "@/lib/router-compat";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, CalendarCheck } from "lucide-react";
import { useState } from "react";
import { toast } from "@/hooks/use-toast";

import { STATIC_ASSETS, toWebP } from "@/lib/imageUtils";

const heroSlides: HeroSlide[] = [
  {
    image: STATIC_ASSETS.heroService1_1920,
    imageMobile: STATIC_ASSETS.heroService1_640,
    title: "RV Service",
    subtitle: "Certified technicians keeping your RV road-ready",
  },
  {
    image: STATIC_ASSETS.heroService2_1920,
    imageMobile: STATIC_ASSETS.heroService2_640,
    title: "After the Sale, It's the Service That Counts!",
    subtitle: "Over 50 years combined RV service experience",
  },
];

const services = [
  { emoji: "🔍", title: "Roof Inspection (Non-Repair)", desc: "Inspect roof for needed repairs and/or sealants.", price: "Free" },
  { emoji: "🛣️", title: "OPEN ROAD Package", desc: "Full pre-trip inspection including chassis, brakes, suspension, electrical, water, LP gas, and overall condition.", price: "$780.00" },
  { emoji: "❄️", title: "RV Winterization", desc: "Drain and pressurize fresh water system with up to 2 gallons of RV antifreeze.", price: "Starting at $195.00" },
  { emoji: "🌀", title: "A/C Service", desc: "Clean coils, check amp draw, temperature drop, filters, shroud condition, and unclog drains.", price: "$195.00" },
  { emoji: "📦", title: "Prepare for Winter Storage", desc: "Drain tanks, flush systems, inspect roof, and pressurize with antifreeze.", price: "$649.00" },
  { emoji: "🔋", title: "Battery Load Test", desc: "Inspect, top off fluids, load test, clean terminals, and apply protector.", price: "$98.50" },
  { emoji: "⚡", title: "Generator Service", desc: "Recommended at 20–50 hrs first service, then every 150–250 hrs depending on fuel type.", price: "$195.00" },
  { emoji: "↔️", title: "Slide-Out Service (Per Slide)", desc: "Check seals, apply conditioner, and lubricate rails/drives.", price: "$197.00" },
  { emoji: "⚙️", title: "Wheel Bearing Repack", desc: "For one axle. Price does not include parts.", price: "Starting at $195.00/axle" },
  { emoji: "🔥", title: "Water Heater Service", desc: "Check anode rod, relief valve, burners, electrodes, thermocouple, module boards, and flush tank.", price: "$295.50" },
  { emoji: "🌡️", title: "Absorption Refrigerator Service", desc: "Verify venting, clean burner/orifice, inspect seals, check LP pressure and voltage.", price: "$195.00" },
  { emoji: "🔥", title: "Furnace Service", desc: "Inspect furnace, check operation, clean internal and external components.", price: "$390.00" },
  { emoji: "💧", title: "Seal-Tech Check", desc: "Water leak detection test.", price: "$195.00" },
  { emoji: "🚽", title: "Black & Grey Tank Service", desc: "Drain, flush, and sanitize tanks.", price: "$349.00" },
  { emoji: "🌿", title: "Spring De-Winterization", desc: "Prepare your RV for the season with full system flush and inspection.", price: "Starting at $195.00" },
];

const serviceOfferings = [
  "RV Roof Seals & Inspections",
  "LP Gas Filling Station",
  "Battery Checks & Replacements",
  "Trailer Wheel Bearings Checked & Packed",
  "Brake Controllers Installation",
  "Full Systems Test (Electrical, LP, Water System, RV Appliances)",
  "Walk-Thru (Orientation on How to Operate Your Unit)",
  "Wiper Blade Replacement",
  "Winterization (Guaranteed not to Freeze) & Spring Flush",
  "Slide-Out Systems",
  "Awnings & Slide Toppers",
  "Insurance Estimates",
];

const serviceImages = [
  toWebP("/images/site/service-gallery-1.png", { w: 500, q: 55 }),
  toWebP("/images/site/service-gallery-2.png", { w: 500, q: 55 }),
  toWebP("/images/site/service-gallery-3.png", { w: 500, q: 55 }),
  toWebP("/images/site/service-gallery-4.png", { w: 500, q: 55 }),
  toWebP("/images/site/service-gallery-5.png", { w: 500, q: 55 }),
];

const EMPTY_FORM = { name: "", email: "", phone: "", zip: "", contact: "phone", time: "morning", serviceType: "" };
const CRM_ENDPOINT = `${import.meta.env['VITE_SUPABASE_URL']}/functions/v1/website-lead`;

const Service = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const [first_name, ...rest] = form.name.trim().split(/\s+/);
      const res = await fetch(CRM_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY']}`,
        },
        body: JSON.stringify({
          first_name: first_name || form.name.trim(),
          last_name: rest.join(" "),
          email: form.email.trim(),
          phone_mobile: form.phone.trim(),
          zip: form.zip.trim(),
          country: "US",
          comments: [
            `Service needed: ${form.serviceType.trim()}`,
            `Preferred contact: ${form.contact}`,
            `Preferred time: ${form.time}`,
          ].join("\n"),
          page_url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : "",
          source_display: "Service Request Form",
        }),
      });
      const data = await res.json().catch(() => ({ success: res.ok }));
      if (!res.ok || data.success === false) {
        toast({
          title: "Could not submit request",
          description: data.error || "Please call us at (910) 793-2894 and we'll get you scheduled.",
          variant: "destructive",
        });
        return;
      }
      toast({ title: "Service request submitted!", description: "We'll contact you to confirm your appointment." });
      setForm(EMPTY_FORM);
    } catch (err: any) {
      toast({
        title: "Network error",
        description: err?.message || "Could not reach the server. Please call (910) 793-2894.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <Helmet>
        {/* Title/description/canonical/og live in the route head() in
            src/routes/rv-service.tsx — the single SSR-visible source. */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "RV Service & Repair",
          provider: {
            "@type": "AutoDealer",
            name: "Rex & Sons RVs",
            telephone: "+1-910-793-2894",
            address: {
              "@type": "PostalAddress",
              streetAddress: "5625 Barbados Blvd",
              addressLocality: "Castle Hayne",
              addressRegion: "NC",
              postalCode: "28429",
              addressCountry: "US",
            },
          },
          areaServed: "Wilmington, NC",
          serviceType: "RV Repair and Maintenance",
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "RV Service Packages",
            itemListElement: services.map((s) => ({
              "@type": "Offer",
              itemOffered: { "@type": "Service", name: s.title, description: s.desc },
              price: s.price,
              priceCurrency: "USD",
            })),
          },
        })}</script>
      </Helmet>
      <HeroSlider cmsSlug="services" slides={heroSlides} height="min-h-[400px]">
        <div className="flex flex-wrap gap-3">
          <Button asChild size="lg" className="font-heading uppercase tracking-wide text-center">
            <a href="tel:910-793-2894"><Phone className="mr-2 h-4 w-4" />Call: 910-793-2894</a>
          </Button>
          <Button asChild size="lg" variant="secondary" className="font-heading uppercase tracking-wide">
            <Link to="/schedule-appointment"><CalendarCheck className="mr-2 h-4 w-4" />Book Online</Link>
          </Button>
        </div>
      </HeroSlider>

      {/* Cape Fear Custom Announcement */}
      <section className="bg-accent/10 py-8">
        <div className="container text-center">
          <h2 className="font-heading text-xl font-bold uppercase text-foreground">🎉 Exciting News for RV Enthusiasts!</h2>
          <p className="mx-auto mt-2 max-w-3xl text-muted-foreground">
            Rex & Sons RVs welcomes <strong>Cape Fear Custom RV Service</strong>. Cape Fear Custom RV Service has joined the Rex & Sons RVs team, bringing together decades of expertise for recreational vehicles. Whether it's routine maintenance, repairs, or custom modifications, the combined talents ensure top-notch care for all your RV needs.
          </p>
        </div>
      </section>

      {/* Service Packages */}
      <LazySection fallback={<div className="min-h-[420px]" />}>
<section className="py-16">
        <div className="container">
          <div className="mx-auto max-w-5xl">
            <div className="mb-8 rounded-xl border-l-4 border-primary bg-muted p-6">
              <h3 className="font-heading text-lg font-bold uppercase text-foreground">"The Difference is Our People!"</h3>
              <p className="mt-2 text-muted-foreground">
                We care about your RV lifestyle! From the Customer Service & Parts Counter to the Service Bays, we go the extra mile for each and every customer, on any make or model. As a member of <strong>Route 66</strong>, North America's largest network of RV dealers, we have access to search the country to find most any replacement part needed to repair your RV.
              </p>
            </div>

            <h2 className="font-heading text-3xl font-bold uppercase text-foreground text-center">Service <span className="text-primary">Packages</span></h2>
            <p className="mt-2 text-center text-sm text-muted-foreground">Not all inspections and services have parts pricing included. If any repairs require additional time or parts, an estimate will be provided.</p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {services.map((s, i) => (
                <div
                  key={s.title}
                  className="rounded-lg border bg-card p-5 shadow-xs transition hover:shadow-md flex flex-col"
                >
                  <span className="text-2xl">{s.emoji}</span>
                  <h3 className="mt-2 font-heading text-base font-bold text-card-foreground">{s.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground flex-1">{s.desc}</p>
                  <p className="mt-3 font-heading text-lg font-bold text-primary">{s.price}</p>
                  <Button asChild size="sm" className="mt-3 w-full font-heading uppercase text-xs tracking-wide">
                    <Link to="/schedule-appointment"><CalendarCheck className="mr-1.5 h-3.5 w-3.5" />Schedule Appointment</Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
</LazySection>

      {/* Service Offerings List */}
      <LazySection fallback={<div className="min-h-[420px]" />}>
<section className="bg-muted py-16">
        <div className="container">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-heading text-2xl font-bold uppercase text-foreground text-center">Our Service Offerings</h2>
            <div className="mt-8 grid gap-2 sm:grid-cols-2">
              {serviceOfferings.map((item) => (
                <div key={item} className="flex items-center gap-2 rounded-lg border bg-card px-4 py-3">
                  <span className="text-primary">✓</span>
                  <span className="text-sm text-card-foreground">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
</LazySection>

      {/* Service Gallery */}
      <LazySection fallback={<div className="min-h-[420px]" />}>
<section className="py-16">
        <div className="container">
          <h2 className="font-heading text-2xl font-bold uppercase text-foreground text-center mb-8">Our Service <span className="text-primary">Facility</span></h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {serviceImages.map((img, i) => (
              <div key={i} className="overflow-hidden rounded-xl shadow-md">
                <img src={img} alt={`Rex & Sons RV Service Facility ${i + 1}`} className="h-48 w-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Button asChild size="lg" variant="outline" className="font-heading uppercase">
              <Link to="/rv-manuals">📖 Browse RV Component Manuals</Link>
            </Button>
          </div>
        </div>
      </section>
</LazySection>

      {/* Service Request Form */}
      <LazySection fallback={<div className="min-h-[420px]" />}>
<section className="bg-muted py-16">
        <div className="container">
          <div className="mx-auto max-w-2xl">
            <h2 className="font-heading text-2xl font-bold uppercase text-foreground text-center">Request Service <span className="text-primary">Appointment</span></h2>
            <form onSubmit={handleSubmit} className="mt-8 space-y-5 rounded-xl border bg-card p-6 shadow-xs">
              <div className="grid gap-5 sm:grid-cols-2">
                <div><Label htmlFor="sname">Name *</Label><Input id="sname" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label htmlFor="semail">Email *</Label><Input id="semail" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div><Label htmlFor="sphone">Phone *</Label><Input id="sphone" type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label htmlFor="szip">Zip Code *</Label><Input id="szip" required value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} /></div>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="scontact">Best way to contact</Label>
                  <Select value={form.contact} onValueChange={(v) => setForm({ ...form, contact: v })}>
                    <SelectTrigger id="scontact"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="stime">Best time to contact</Label>
                  <Select value={form.time} onValueChange={(v) => setForm({ ...form, time: v })}>
                    <SelectTrigger id="stime"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Morning</SelectItem>
                      <SelectItem value="afternoon">Afternoon</SelectItem>
                      <SelectItem value="evening">Evening</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="stype">What type of service do you need? *</Label>
                <Textarea id="stype" rows={3} required value={form.serviceType} onChange={(e) => setForm({ ...form, serviceType: e.target.value })} />
              </div>
              <Button type="submit" size="lg" disabled={submitting} className="w-full font-heading uppercase tracking-wide">{submitting ? "Submitting…" : "Submit Service Request"}</Button>
            </form>
          </div>
        </div>
      </section>
</LazySection>

      {/* CTA */}
      <LazySection fallback={<div className="min-h-[420px]" />}>
<section className="bg-primary py-12">
        <div className="container text-center">
          <h2 className="font-heading text-2xl font-bold uppercase text-primary-foreground">Expect "Pit Stop Service"!</h2>
          <p className="mt-2 text-primary-foreground/80">Call us today to schedule your appointment.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" variant="secondary" className="font-heading uppercase">
              <a href="tel:910-793-2894">Call (910) 793-2894</a>
            </Button>
            <Button asChild size="lg" variant="secondary" className="font-heading uppercase">
              <Link to="/schedule-appointment"><CalendarCheck className="mr-2 h-4 w-4" />Book Appointment</Link>
            </Button>
          </div>
        </div>
      </section>
</LazySection>
    </Layout>
  );
};

export default Service;
